import React, { useEffect, useRef, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import clsx from 'clsx';
import { Modal } from './ui/Modal';
import { useCreateTag } from '../services/tagService';
import { useCurrentUser } from '../services/userHooks';
import { useToast } from '../hooks/useToast';

interface AddTagModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function AddTagModal({ isOpen, onClose }: AddTagModalProps) {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { currentUser } = useCurrentUser();
  const createTagMutation = useCreateTag();
  const { showToast } = useToast();

  useEffect(() => {
    if (isOpen) {
      setName('');
      setError(null);
    }
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (error) {
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!currentUser) {
      setError(t`User not logged in`);
      return;
    }

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(t`Tag name is required`);
      return;
    }

    if (trimmedName.length > 250) {
      setError(t`Tag name must be 250 characters or less`);
      return;
    }

    try {
      await createTagMutation.mutateAsync({
        name: trimmedName,
        formKey: currentUser.formKey,
      });
      showToast(t`${trimmedName} tag created`, 'success');
      onClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t`Sorry, there was an error. Please try again.`;
      setError(errorMessage);
    }
  };

  return (
    <Modal.Root
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={t`Add tag`}
      initialFocusRef={inputRef}
      size="xl"
    >
      <Modal.Header title={t`Add tag`} />
      <Modal.Body>
        <form onSubmit={handleSubmit} className="flex flex-row gap-4">
          <input
            ref={inputRef}
            type="text"
            name="name"
            aria-label={t`Tag name`}
            value={name}
            onChange={handleInputChange}
            placeholder={t`Tag name`}
            className={clsx('form-input', error && 'form-input-error')}
            maxLength={250}
            disabled={createTagMutation.isPending}
          />

          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={createTagMutation.isPending}
            className={clsx(
              'button-primary px-12 py-4',
              createTagMutation.isPending && 'bg-surface-secondary',
            )}
          >
            {createTagMutation.isPending ? <Trans>Adding...</Trans> : <Trans>Add</Trans>}
          </button>
        </form>
      </Modal.Body>
    </Modal.Root>
  );
}

export default AddTagModal;
